import { useMemo } from "react";
import { Box, Type, Sparkles, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ConfigComponent, ComponentCategory } from "./types";

// Category configuration
const COMPONENT_CATEGORIES: Record<ComponentCategory, {
  label: string;
  icon: typeof Box;
  keywords: string[];
}> = {
  stones: {
    label: 'Kivet',
    icon: Box,
    keywords: ['pohjakivi', 'taulukivi', 'koroke', 'kivikuutio', 'paasi', 'nimilaatta', 'laatta']
  },
  engravings: {
    label: 'Kaiverrukset', 
    icon: Type,
    keywords: ['kaiverrus', 'nimi', 'teksti', 'muistolause']
  },
  accessories: {
    label: 'Lisävarusteet',
    icon: Sparkles,
    keywords: ['lyhty', 'maljakko', 'koriste', 'kultaus', 'ornamentti']
  }
};

function categorizeComponent(name: string, label: string): ComponentCategory {
  const searchText = `${name} ${label}`.toLowerCase();
  
  for (const [key, config] of Object.entries(COMPONENT_CATEGORIES)) {
    if (config.keywords.some(kw => searchText.includes(kw))) {
      return key as ComponentCategory;
    }
  }
  
  return 'accessories'; // Default category
}

interface ComponentPickerCardProps {
  component: ConfigComponent;
  isAdditional: boolean;
  onAdd: () => void;
}

function ComponentPickerCard({ component, isAdditional, onAdd }: ComponentPickerCardProps) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-auto p-3 flex flex-col items-start gap-1 hover:bg-primary/5 hover:border-primary/50 transition-colors"
      onClick={onAdd}
    >
      <div className="flex items-center gap-2 w-full">
        <Plus className="h-4 w-4 text-primary" />
        <span className="font-medium text-sm truncate">{component.componentTypeLabel}</span>
      </div>
      {isAdditional ? (
        <span className="text-xs text-muted-foreground">+ Lisää toinen</span>
      ) : component.allowedMaterials.length > 0 ? (
        <span className="text-xs text-muted-foreground">
          {component.allowedMaterials.length} materiaalia
        </span>
      ) : null}
    </Button>
  );
}

interface ComponentPickerProps {
  availableComponents: ConfigComponent[];
  activeComponentTypeIds?: Set<string>;
  onAddComponent: (componentId: string) => void;
}

export function ComponentPicker({ availableComponents, activeComponentTypeIds, onAddComponent }: ComponentPickerProps) {
  // Group components by category
  const grouped = useMemo(() => {
    const groups: Record<ComponentCategory, ConfigComponent[]> = {
      stones: [],
      engravings: [],
      accessories: []
    };
    
    availableComponents.forEach(c => {
      const category = categorizeComponent(c.componentTypeName, c.componentTypeLabel);
      groups[category].push(c);
    });
    
    return groups;
  }, [availableComponents]);

  // Find first non-empty category for default tab
  const defaultTab = useMemo(() => {
    if (grouped.stones.length > 0) return 'stones';
    if (grouped.engravings.length > 0) return 'engravings';
    return 'accessories';
  }, [grouped]);

  const totalAvailable = availableComponents.length;

  if (totalAvailable === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6 text-center text-muted-foreground">
          Kaikki komponentit on lisätty tilaukseen
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Plus className="h-5 w-5" />
          Lisää komponentti
          <span className="text-sm font-normal text-muted-foreground">
            ({totalAvailable} saatavilla)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={defaultTab}>
          <TabsList className="grid w-full grid-cols-3">
            {(Object.entries(COMPONENT_CATEGORIES) as [ComponentCategory, typeof COMPONENT_CATEGORIES.stones][]).map(([key, config]) => {
              const Icon = config.icon;
              const count = grouped[key].length;
              return (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  disabled={count === 0}
                  className="gap-2"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{config.label}</span>
                  {count > 0 && (
                    <span className="text-xs bg-muted px-1.5 rounded-full">{count}</span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
          
          {(Object.keys(COMPONENT_CATEGORIES) as ComponentCategory[]).map(category => (
            <TabsContent key={category} value={category} className="mt-4">
              {grouped[category].length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {grouped[category].map(c => (
                    <ComponentPickerCard 
                      key={c.id} 
                      component={c}
                      isAdditional={activeComponentTypeIds?.has(c.componentTypeId) ?? false}
                      onAdd={() => onAddComponent(c.id)} 
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  Ei saatavilla olevia komponentteja
                </p>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
